import React from 'react';

import { usePageStore } from '@client/store/pageStore/context/pageContext';
import i18n from '@client/utils/i18n';
import { SpectralRanges } from '@ledium/types';
import ArticleIcon from '@mui/icons-material/Article';
import { IconButton, Paper, Popper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import Typography, { TypographyProps } from '@mui/material/Typography';

import { If } from './If';

interface SpectrogramWithRanges {
    pfd: SpectralRanges;
    irradiance: SpectralRanges;
}

interface DetailedIntensityInfoProps {
    spectrogram: SpectrogramWithRanges;
    labelKey?: string | null;
    label?: React.ReactNode;
    unit?: 'pfd' | 'irradiance';
    typography?: TypographyProps['variant'];
    iconOnly?: boolean;
}

const genId = () => Date.now().toString(36) + Math.random().toString(36);
// const spectrumOrder = ['uvA', 'uvB', 'uvC', 'uv', 'blue', 'green', 'red', 'fr', 'ir', 'visibleSum', 'sum'];
const spectrumOrder = ['uv', 'blue', 'green', 'red', 'fr', 'ir', 'visibleSum', 'sum'];

export const DetailedIntensityInfo = ({
    spectrogram,
    labelKey = null,
    label = null,
    unit = 'irradiance',
    typography = 'body2',
    iconOnly = false
}: DetailedIntensityInfoProps) => {
    const {
        pageState: { formData },
        pageActions
    } = usePageStore();
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
    const [id, setId] = React.useState<string | null>(null);

    React.useEffect(() => {
        setId(genId());
    }, []);

    const onClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(anchorEl ? null : event.currentTarget);
        pageActions.formData.updateFormItem({ openedDetailedIntensityPopperId: id });
    };

    React.useEffect(() => {
        if (id !== formData.openedDetailedIntensityPopperId) {
            setAnchorEl(null);
        }
    }, [formData.openedDetailedIntensityPopperId, id]);

    return (
        <Typography display="inline" variant={typography}>
            <If condition={!iconOnly}>
                <If condition={!!labelKey}>{i18n(labelKey as string)}</If>
                <If condition={!!label}>{label}</If>
                <b> {spectrogram[unit].sum}</b>
                <span> {i18n(`${unit}_unit`)}</span>
            </If>
            <IconButton size="small" color="primary" onClick={onClick}>
                <ArticleIcon fontSize="inherit" />
            </IconButton>
            <Popper open={!!anchorEl} anchorEl={anchorEl} style={{ zIndex: 1000 }}>
                <TableContainer
                    component={Paper}
                    sx={{ border: 1 }}
                    onClick={onClick as unknown as React.MouseEventHandler<HTMLDivElement>}
                    style={{ cursor: 'pointer' }}
                >
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell style={{ padding: '3px 10px' }}>Range</TableCell>
                                <TableCell style={{ padding: '3px 10px' }} align="right">
                                    {i18n(unit + '_unit')}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {spectrumOrder.map((name) => (
                                <TableRow key={name}>
                                    <TableCell style={{ padding: '3px 10px' }}>{i18n('detail_' + name)}</TableCell>
                                    <TableCell style={{ padding: '3px 10px' }} align="right">
                                        {spectrogram[unit][name as keyof SpectralRanges]}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Popper>
        </Typography>
    );
};
